import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Instagram, Facebook } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";

// Logo transparente - mesmo do header
const LOGO_URL = "/images/justfit-logo-280.webp?v=2026";

const FALLBACK = {
  maia: { address: "Vereda Ponte de Parada 135, 4425-294 Águas Santas", phone: "932 317 197", schedule: [{ dias: "Seg-Sex", horario: "6h30-22h" }, { dias: "Sáb", horario: "9h-20h" }, { dias: "Dom", horario: "9h-13h" }] },
  barcelos: { address: "Rua Cândido da Cunha 98, 4750-276 Barcelos", phone: "932 317 195", schedule: [{ dias: "Seg-Sex", horario: "7h-22h" }, { dias: "Sáb", horario: "9h-20h" }, { dias: "Dom", horario: "9h-13h" }] },
};

const ScheduleLines = ({ schedule }) => (
  <span>
    {schedule.map((s, i) => (
      <span key={i}>{s.dias}: {s.horario}{i < schedule.length - 1 ? <br /> : null}</span>
    ))}
  </span>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [info, setInfo] = useState(FALLBACK);

  useEffect(() => {
    ["maia", "barcelos"].forEach((c) => {
      axios.get(`/api/content/club-info?club=${c}`)
        .then((res) => {
          if (res.data) setInfo((prev) => ({ ...prev, [c]: {
            address: res.data.address || prev[c].address,
            phone: res.data.phone || prev[c].phone,
            schedule: res.data.schedule?.length ? res.data.schedule : prev[c].schedule,
          }}));
        })
        .catch(() => {});
    });
  }, []);

  return (
    <footer className="bg-[#1A1A1A] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <img src={LOGO_URL} alt="Justfit" width="400" height="171" className="h-14 w-auto mb-6" />
            <p className="text-gray-400 text-sm leading-relaxed">
              Ginásios premium com qualidade de excelência. Mais de 100 aulas de grupo por semana, 
              nutrição e personal training. Tudo começa aqui!
            </p>
          </motion.div>

          {/* Maia Club */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="text-white font-bold uppercase tracking-wider mb-6" role="heading" aria-level="3">
              Clube da <span className="text-[#FED601]">Maia</span>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={18} className="text-[#FED601] flex-shrink-0 mt-0.5" />
                <span>{info.maia.address}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone size={18} className="text-[#FED601] flex-shrink-0" />
                <span>{info.maia.phone}</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Clock size={18} className="text-[#FED601] flex-shrink-0 mt-0.5" />
                <ScheduleLines schedule={info.maia.schedule} />
              </li>
            </ul>
            <Link 
              to="/clube/maia" 
              className="inline-block mt-4 text-[#FED601] text-sm font-medium hover:underline"
            >
              Ver Clube →
            </Link>
            <div className="flex gap-3 mt-4">
              <a href="https://facebook.com/justfitmaia" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FED601] hover:text-black transition-colors duration-300" aria-label="Facebook Justfit Maia" data-testid="maia-facebook">
                <Facebook size={16} />
              </a>
              <a href="https://instagram.com/justfitparada" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FED601] hover:text-black transition-colors duration-300" aria-label="Instagram Justfit Maia" data-testid="maia-instagram">
                <Instagram size={16} />
              </a>
            </div>
          </motion.div>

          {/* Barcelos Club */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="text-white font-bold uppercase tracking-wider mb-6" role="heading" aria-level="3">
              Clube de <span className="text-[#FED601]">Barcelos</span>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={18} className="text-[#FED601] flex-shrink-0 mt-0.5" />
                <span>{info.barcelos.address}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone size={18} className="text-[#FED601] flex-shrink-0" />
                <span>{info.barcelos.phone}</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Clock size={18} className="text-[#FED601] flex-shrink-0 mt-0.5" />
                <ScheduleLines schedule={info.barcelos.schedule} />
              </li>
            </ul>
            <Link 
              to="/clube/barcelos" 
              className="inline-block mt-4 text-[#FED601] text-sm font-medium hover:underline"
            >
              Ver Clube →
            </Link>
            <div className="flex gap-3 mt-4">
              <a href="https://facebook.com/justfitbarcelos" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FED601] hover:text-black transition-colors duration-300" aria-label="Facebook Justfit Barcelos" data-testid="barcelos-facebook">
                <Facebook size={16} />
              </a>
              <a href="https://instagram.com/justfitparada" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FED601] hover:text-black transition-colors duration-300" aria-label="Instagram Justfit Barcelos" data-testid="barcelos-instagram">
                <Instagram size={16} />
              </a>
            </div>
          </motion.div>

          {/* Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="text-white font-bold uppercase tracking-wider mb-6" role="heading" aria-level="3">
              Links <span className="text-[#FED601]">Úteis</span>
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/#clubes" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Os Nossos Clubes
                </a>
              </li>
              <li>
                <a href="/#servicos" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Serviços
                </a>
              </li>
              <li>
                <a href="/#personal" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Personal Training
                </a>
              </li>
              <li>
                <a href="/#testemunhos" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Testemunhos
                </a>
              </li>
              <li>
                <a href="/#contacto" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Contacto
                </a>
              </li>
              <li>
                <a href="https://www.justfitcenter.pt/privacy/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Política de Privacidade
                </a>
              </li>
              <li>
                <a href="https://www.justfitcenter.pt/terms/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#FED601] transition-colors text-sm">
                  Termos de Utilização
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            © {currentYear} Justfit Fitness Center - Supremoritual Centros de Fitness, Lda. Todos os direitos reservados.
          </p>
          <a
            href="https://www.livroreclamacoes.pt/Pedido/Reclamacao"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-[#FED601] transition-colors text-sm"
          >
            Livro de Reclamações
          </a>
        </div>
      </div>
    </footer>
  );
};
